import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { startOfDay, addYears } from "date-fns";
import { Box, Button, Theme, Typography, useMediaQuery } from "@mui/material";
import truncate from "lodash/truncate";
import { InvestmentTypeEnum, useGetQuotesV2 } from "@bn/api/apiv1";
import { useGetSecurity } from "@bn/api/quotes";
import {
  AccountsHeader,
  CardHeading,
  CardPageLayout,
  ComparisonValue,
  GainLossPercents,
  InvestmentChart,
} from "@components";
import { ButtonWithActionMenu } from "@components/ButtonWithActionMenu";
import { SecurityImage } from "@components/SecurityImage";
import AddIcon from "@icons/myicons/plus.svg?react";
import { getValueByInvestmentAndBookingType } from "@helpers";
import { useSecurityImages } from "@hooks";
import { FundamentalsPageByIsin } from "../../fundamentals";
import { SecurityActionMenuItems } from "../components/SecurityActionMenu";
import { useChartData } from "../../investments/investment/hooks/useChartData";

const availableIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "YTD",
  "oneYear",
  "threeYears",
  "fiveYears",
  "custom",
];
const availableCompactIntervalTypes: IntervalType[] = [
  "oneDay",
  "oneWeek",
  "oneMonth",
  "oneYear",
  "fiveYears",
  "custom",
];

const SecurityChart = styled(InvestmentChart)(({ theme }) => ({
  minHeight: 546,

  [theme.breakpoints.down("lg")]: {
    minHeight: 427,
  },

  [theme.breakpoints.down("sm")]: {
    boxShadow: "none",
  },
}));

const ChartTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(5),

  [theme.breakpoints.down("md")]: {
    marginBottom: 0,
  },
}));

export const SingleSecurityPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMoreMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const id = searchParams.get("id");
  const quoteProvider = searchParams.get("quoteProvider");
  const enabled = Boolean(id && quoteProvider);

  useEffect(() => {
    if (!enabled) {
      navigate("/");
    }
  }, [enabled, navigate]);

  const { data: securityData } = useGetSecurity(
    { id: id!, quoteProvider: quoteProvider! },
    { enabled }
  );
  const { data: quoteData } = useGetQuotesV2(
    { securityId: [id!], quoteProvider: quoteProvider ?? "" },
    { enabled }
  );

  const security = useMemo(
    () => ({ ...securityData?.data, ...securityData?.data?.attributes }),
    [securityData]
  );
  const quote = quoteData?.quotes?.[0];

  const isin = security?.isin ?? undefined;
  const tickerSymbol = quote?.tickerSymbol ?? security?.ticker_symbol ?? "";
  const currency = quote?.currency || "EUR";
  const securityType = security?.security_type;

  const [currentInterval, setCurrentInterval] =
    useState<IntervalType>("oneYear");
  const [chartStartDate, setChartStartDate] = useState<Date | undefined>(
    startOfDay(addYears(new Date(), -1))
  );
  const [chartEndDate, setChartEndDate] = useState<Date | undefined>(
    new Date()
  );
  const [comparedTicker, setComparedTicker] = useState<ComparisonValue>();

  const { data: chartData = [], isFetching } = useChartData({
    currentInterval,
    tickerSymbol,
    tickerSymbolWithCurrency: tickerSymbol || undefined,
    quoteProvider: quoteProvider ?? undefined,
    startDate: chartStartDate,
    endDate: chartEndDate,
    comparedTickerSymbol: comparedTicker?.value,
    currency,
  });

  const securityForLogo = useMemo(
    () =>
      isin
        ? [
            {
              isin,
              hasValidIsin: true,
              type: securityType ?? "11_stock",
              tickerSymbol,
              quoteProvider: quoteProvider ?? undefined,
            },
          ]
        : undefined,
    [isin, securityType, tickerSymbol, quoteProvider]
  );
  const images = useSecurityImages(securityForLogo);
  const image = images[isin || ""] || images[tickerSymbol];

  const chartHeaderValue =
    getValueByInvestmentAndBookingType({
      value: quote?.last ?? 0,
      investmentType: (securityType as InvestmentTypeEnum) ?? "11_stock",
      currency,
      percentageRange: "0-100",
      quoteNotation: quote?.priceUnit,
    }) ?? 0;

  const addToDepotUrl = `/new-transaction-wizard?quoteProvider=${quoteProvider}&tickerSymbol=${tickerSymbol}`;

  const breadcrumbs: BreadcrumbItem[] = [
    { to: "/", text: t("breadcrumbs.home") },
    { text: t("singleSecurityPage.breadcrumbs.quotes") },
    { text: truncate(security?.name) ?? "" },
  ];

  const handleRangeSelect = (type: IntervalType, value?: RangeType) => {
    setCurrentInterval(type);
    setChartStartDate(value?.startDate);
    setChartEndDate(value?.endDate);

    if (type === "oneDay") {
      setComparedTicker(undefined);
    }
  };

  return (
    <CardPageLayout
      data-testid="single-security-page"
      breadcrumbs={breadcrumbs}
      cardHeading={
        <CardHeading
          actionsInline
          title={
            <AccountsHeader
              title={security?.name}
              subtitle={
                [isin, security?.wkn].filter(Boolean).join(" · ") || undefined
              }
              icon={<SecurityImage src={image} alt={security?.name} />}
            />
          }
          actions={
            // The menu only repeats the single action, so it is the narrow
            // screen stand-in for the button rather than a companion to it.
            isMoreMd ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon width="18px" height="12px" />}
                onClick={() => navigate(addToDepotUrl)}
                data-testid="add-to-depot-button"
              >
                {t("singleSecurityPage.actions.addToDepot")}
              </Button>
            ) : (
              <ButtonWithActionMenu
                testId="security-action-menu-button"
                buttonColor="tertiary"
              >
                <SecurityActionMenuItems
                  onAddToDepot={() => navigate(addToDepotUrl)}
                />
              </ButtonWithActionMenu>
            )
          }
        />
      }
      cardChildren={
        <SecurityChart
          drawLines={!isSm}
          isFetching={isFetching}
          data={chartData}
          availableDateRanges={availableIntervalTypes}
          availableCompactDateRanges={availableCompactIntervalTypes}
          title={
            <ChartTitle>
              <Typography variant="body2">
                {t("investment.header.chart.subheader")}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h2">{chartHeaderValue}</Typography>
                <GainLossPercents value={quote?.changePercent ?? 0} />
              </Box>
            </ChartTitle>
          }
          comparedTicker={comparedTicker}
          onComparedTickerChange={setComparedTicker}
          onSelect={handleRangeSelect}
        />
      }
    >
      {isin && (
        <FundamentalsPageByIsin
          isin={isin}
          securityType={securityType}
          quoteProvider={quoteProvider ?? undefined}
          tickerSymbol={tickerSymbol}
          currency={currency}
        />
      )}
    </CardPageLayout>
  );
};
